import React, { useState, useRef } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
  Dribbble,
  ArrowUpRight,
  Save,
  User,
  Briefcase,
  FileText,
  Link2,
} from "lucide-react";



const EMPTY_FORM = {
  id: null,
  name: "",
  role: "",
  bio: "",
  image: "",

};

const DEFAULT_TEAM = [
  {
    id: 1,
    name: "Alisa Hester",
    role: "Founder & CEO",
    bio: "Former co-founder of Opendoor. Early staff at Spotify and Clearbit.",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&h=600&fit=crop&auto=format&q=80",
 
  },
  {
    id: 2,
    name: "Rich Wilson",
    role: "Engineering Manager",
    bio: "Lead engineering teams at Figma, Pitch, and Protocol Labs.",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&h=600&fit=crop&auto=format&q=80",
 
  },
  {
    id: 3,
    name: "Annie Stanley",
    role: "Product Manager",
    bio: "Former PM for Airtable, Medium, Ghost, and Lumi.",
    image:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=500&h=600&fit=crop&auto=format&q=80",
    
  },
];

export default function TeamAdminManager() {
  const [team, setTeam] = useState(DEFAULT_TEAM);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setForm(member);
    setIsEditing(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.image || !form.name.trim() || !form.role.trim()) return;

    if (isEditing) {
      setTeam((prev) =>
        prev.map((m) => (m.id === form.id ? { ...form } : m))
      );
    } else {
      setTeam((prev) => [...prev, { ...form, id: Date.now() }]);
    }
    closeModal();
  };

  const confirmDelete = (id) => setDeleteTarget(id);
  const cancelDelete = () => setDeleteTarget(null);
  const executeDelete = () => {
    setTeam((prev) => prev.filter((m) => m.id !== deleteTarget));
    setDeleteTarget(null);
  };

  return (
    <div className="tam-wrapper">
      {/* Header with APIC ProtoHub branding */}
      <div className="tam-header">
        <div>
          <div className="tam-brand">
            <span className="tam-brand-icon">⚡</span>
            <span className="tam-brand-text">APIC ProtoHub</span>
            <span className="tam-brand-badge">ANDHRA PRADESH</span>
          </div>
          <h2 className="tam-title">Team Members</h2>
          <p className="tam-subtitle">
            Discover. Design. Prototype. Innovate. — Manage your team profiles.
          </p>
        </div>
        <button className="tam-add-btn" onClick={openAddModal}>
          <Plus size={18} />
          <span>Add Team Member</span>
        </button>
      </div>

      {/* Grid */}
      {team.length === 0 ? (
        <div className="tam-empty">
          <User size={36} />
          <p>No team members added yet.</p>
          <button className="tam-add-btn" onClick={openAddModal}>
            <Plus size={18} />
            <span>Add your first member</span>
          </button>
        </div>
      ) : (
        <div className="tam-grid">
          {team.map((member) => (
            <div key={member.id} className="tam-card">
              <div className="tam-card-image-wrap">
                <img
                  src={member.image}
                  alt={member.name}
                  className="tam-card-image"
                />

                {/* Admin actions */}
                <div className="tam-card-actions">
                  <button
                    className="tam-icon-btn"
                    onClick={() => openEditModal(member)}
                    aria-label="Edit member"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    className="tam-icon-btn danger"
                    onClick={() => confirmDelete(member.id)}
                    aria-label="Delete member"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                {/* Bottom gradient overlay */}
                <div className="tam-card-overlay">
                  <div className="tam-name-row">
                    <h3 className="tam-name">{member.name}</h3>
                    <ArrowUpRight size={16} className="tam-arrow" />
                  </div>
                  <p className="tam-role">{member.role}</p>
                  {member.bio && <p className="tam-bio">{member.bio}</p>}

                  
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="tam-overlay" onClick={closeModal}>
          <div className="tam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tam-modal-header">
              <h3>{isEditing ? "Edit Team Member" : "Add Team Member"}</h3>
              <button className="tam-close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form className="tam-form" onSubmit={handleSubmit}>
              <label className="tam-label">
                <ImageIcon size={16} />
                Photo
              </label>

              <div
                className="tam-upload-box"
                onClick={() => fileInputRef.current?.click()}
              >
                {form.image ? (
                  <img
                    src={form.image}
                    alt="preview"
                    className="tam-upload-preview"
                  />
                ) : (
                  <div className="tam-upload-placeholder">
                    <Upload size={24} />
                    <span>Click to upload photo</span>
                    <span className="tam-upload-hint">
                      Recommended: square or portrait image
                    </span>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              <label className="tam-label">
                <User size={16} />
                Full Name
              </label>
              <input
                type="text"
                className="tam-input"
                placeholder="e.g. Alisa Hester"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                required
              />

              <label className="tam-label">
                <Briefcase size={16} />
                Role
              </label>
              <input
                type="text"
                className="tam-input"
                placeholder="e.g. Founder & CEO"
                value={form.role}
                onChange={(e) =>
                  setForm((p) => ({ ...p, role: e.target.value }))
                }
                required
              />

              <label className="tam-label">
                <FileText size={16} />
                Short Bio
              </label>
              <textarea
                className="tam-textarea"
                placeholder="Brief background or previous experience"
                rows="2"
                value={form.bio}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bio: e.target.value }))
                }
              />
              <button type="submit" className="tam-submit-btn">
                <Save size={18} />
                <span>{isEditing ? "Save Changes" : "Add Member"}</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="tam-overlay" onClick={cancelDelete}>
          <div className="tam-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="tam-confirm-icon">
              <Trash2 size={22} />
            </div>
            <h4>Remove this team member?</h4>
            <p>This action cannot be undone.</p>
            <div className="tam-confirm-actions">
              <button className="tam-cancel-btn" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="tam-delete-btn" onClick={executeDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      <style>{`
        * {
          box-sizing: border-box;
        }

        .tam-wrapper {
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
            sans-serif;
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 20px 60px;
          color: #0b1f3a;
          background: #f8fafc;
          border-radius: 20px;
        }

        .tam-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .tam-brand-icon {
          font-size: 22px;
          background: #0b1f3a;
          color: #4ade80;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tam-brand-text {
          font-weight: 800;
          font-size: 18px;
          letter-spacing: -0.3px;
          color: #0b1f3a;
        }
        .tam-brand-badge {
          background: #0b1f3a;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 30px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .tam-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }

        .tam-title {
          font-size: 26px;
          font-weight: 800;
          margin: 0 0 6px 0;
        }

        .tam-subtitle {
          font-size: 14.5px;
          color: #64748b;
          margin: 0;
        }

        .tam-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #0b1f3a;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 12px 20px;
          font-size: 14.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
          white-space: nowrap;
        }
        .tam-add-btn:hover {
          background: #142c52;
          transform: translateY(-1px);
        }

        .tam-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          padding: 70px 20px;
          border: 1.5px dashed #d6dee8;
          border-radius: 16px;
          color: #64748b;
          text-align: center;
        }

        .tam-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .tam-card {
          border-radius: 16px;
          overflow: hidden;
          background: #0b1f3a;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .tam-card:hover {
          box-shadow: 0 14px 30px -8px rgba(16, 24, 40, 0.28);
          transform: translateY(-2px);
        }

        .tam-card-image-wrap {
          position: relative;
          width: 100%;
          padding-top: 118%;
          overflow: hidden;
        }

        .tam-card-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tam-card-actions {
          position: absolute;
          top: 12px;
          right: 12px;
          display: flex;
          gap: 8px;
          z-index: 2;
        }

        .tam-icon-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.92);
          color: #0b1f3a;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
        }
        .tam-icon-btn:hover {
          background: #fff;
          transform: scale(1.08);
        }
        .tam-icon-btn.danger:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        .tam-card-overlay {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 18px 18px 16px;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.55) 45%,
            rgba(0, 0, 0, 0.82) 100%
          );
        }

        .tam-name-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .tam-name {
          margin: 0;
          font-size: 16.5px;
          font-weight: 700;
          color: #fff;
        }

        .tam-arrow {
          color: rgba(255, 255, 255, 0.75);
          flex-shrink: 0;
        }

        .tam-role {
          margin: 4px 0 6px 0;
          font-size: 13px;
          font-weight: 700;
          color: #4ade80;
        }

        .tam-bio {
          margin: 0 0 12px 0;
          font-size: 12.5px;
          color: rgba(255, 255, 255, 0.82);
          line-height: 1.5;
        }

        .tam-social-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .tam-social-icon {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: background 0.2s ease, transform 0.15s ease;
        }
        .tam-social-icon:hover {
          background: #16a34a;
          border-color: #16a34a;
          transform: translateY(-2px);
        }

        /* ---------- Modal ---------- */
        .tam-overlay {
          position: fixed;
          inset: 0;
          background: rgba(11, 31, 58, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1000;
          backdrop-filter: blur(2px);
        }

        .tam-modal {
          background: #fff;
          border-radius: 18px;
          width: 100%;
          max-width: 460px;
          max-height: 85vh;
          scrollbar-width:none;
          overflow-y: auto;
          padding: 26px;
        }

        .tam-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .tam-modal-header h3 {
          margin: 0;
          font-size: 19px;
          font-weight: 800;
        }

        .tam-close-btn {
          background: #f1f5f9;
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #475569;
        }
        .tam-close-btn:hover {
          background: #e2e8f0;
        }

        .tam-form {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .tam-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13.5px;
          font-weight: 700;
          color: #334155;
          margin: 14px 0 6px 0;
        }

        .tam-input,
        .tam-textarea {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14.5px;
          color: #0b1f3a;
          outline: none;
          font-family: inherit;
          transition: border-color 0.2s ease;
          resize: vertical;
        }
        .tam-input:focus,
        .tam-textarea:focus {
          border-color: #16a34a;
        }

        .tam-upload-box {
          border: 1.5px dashed #cbd5e1;
          border-radius: 12px;
          cursor: pointer;
          overflow: hidden;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .tam-upload-box:hover {
          border-color: #16a34a;
          background: #f5fbf7;
        }

        .tam-upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 32px 16px;
          color: #64748b;
          font-size: 14px;
          font-weight: 600;
        }
        .tam-upload-hint {
          font-size: 12px;
          font-weight: 400;
          color: #94a3b8;
        }

        .tam-upload-preview {
          width: 100%;
          max-height: 180px;
          object-fit: cover;
          display: block;
        }

        .tam-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #16a34a;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 15px;
          font-weight: 700;
          margin-top: 20px;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .tam-submit-btn:hover {
          background: #128a3e;
        }

        /* ---------- Delete confirm ---------- */
        .tam-confirm-box {
          background: #fff;
          border-radius: 16px;
          padding: 28px;
          max-width: 340px;
          width: 100%;
          text-align: center;
        }
        .tam-confirm-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #fee2e2;
          color: #dc2626;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 14px;
        }
        .tam-confirm-box h4 {
          margin: 0 0 6px 0;
          font-size: 17px;
        }
        .tam-confirm-box p {
          margin: 0 0 20px 0;
          font-size: 13.5px;
          color: #64748b;
        }
        .tam-confirm-actions {
          display: flex;
          gap: 10px;
        }
        .tam-cancel-btn,
        .tam-delete-btn {
          flex: 1;
          padding: 11px;
          border-radius: 10px;
          border: none;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }
        .tam-cancel-btn {
          background: #f1f5f9;
          color: #334155;
        }
        .tam-cancel-btn:hover {
          background: #e2e8f0;
        }
        .tam-delete-btn {
          background: #dc2626;
          color: #fff;
        }
        .tam-delete-btn:hover {
          background: #b91c1c;
        }

        .tam-footer {
          margin-top: 48px;
          padding-top: 24px;
          border-top: 1px solid #e2e8f0;
          text-align: center;
          font-size: 13px;
          color: #64748b;
        }
        .tam-footer-tagline {
          font-weight: 600;
          color: #0b1f3a;
        }

        /* ---------- Responsive ---------- */
        @media (max-width: 900px) {
          .tam-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 560px) {
          .tam-header {
            flex-direction: column;
          }
          .tam-add-btn {
            width: 100%;
            justify-content: center;
          }
          .tam-grid {
            grid-template-columns: 1fr;
          }
          .tam-card-image-wrap {
            padding-top: 90%;
          }
          .tam-modal {
            padding: 20px;
          }
          .tam-brand {
            gap: 6px;
          }
          .tam-brand-text {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
}